/**
 * Worker Cost Calculator — Configuration tab (Admin only).
 * Buildaprice-style template: enable/disable-able on-cost lines, live
 * % and $/h per line, total cost rate = pay rate + on-costs.
 */
import { useEffect, useState, useMemo } from "react";
import api, { fmtNZD } from "@/lib/api";
import NumberInput from "@/components/NumberInput";
import SaveIndicator from "@/components/SaveIndicator";
import useAutosave from "@/hooks/useAutosave";

const TYPE_LABEL = {
  percent_of_pay: "% of pay rate",
  days_per_year: "days / year",
  minutes_per_day: "minutes / day",
  fixed_dollar_per_hour: "$ / hour (fixed)",
};

export default function WorkerCostCalc() {
  const [tpl, setTpl] = useState(null);
  const [payRate, setPayRate] = useState(50);
  const [calc, setCalc] = useState(null);
  const [msg, setMsg] = useState("");
  const [workers, setWorkers] = useState([]);
  const [savingTpl, setSavingTpl] = useState(false);
  const autosave = useAutosave(async (key, patch) => {
    // key is tpl id. patch is the full payload we schedule.
    await api.patch(`/worker-cost-templates/${key}`, patch);
  });
  function scheduleTplSave(nextTpl) {
    autosave.schedule(nextTpl.id, {
      working_days_per_week: nextTpl.working_days_per_week,
      hours_per_day: nextTpl.hours_per_day,
      items: nextTpl.items,
    });
  }

  async function load() {
    const [{ data: list }, { data: w }] = await Promise.all([
      api.get("/worker-cost-templates"),
      api.get("/workers").catch(() => ({ data: [] })),
    ]);
    setTpl(list[0] || null);
    setWorkers(w || []);
  }
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!tpl) return;
    api.post("/worker-cost-templates/calculate", {
      pay_rate: parseFloat(payRate || 0), template_id: tpl.id, items: tpl.items,
      hours_per_day: tpl.hours_per_day ?? null, working_days_per_week: tpl.working_days_per_week ?? null,
    }).then(r => setCalc(r.data)).catch(() => {});
  }, [tpl, payRate]);

  function patchItem(i, patch) {
    setTpl(t => {
      const next = { ...t, items: t.items.map((it, j) => j === i ? { ...it, ...patch } : it) };
      scheduleTplSave(next);
      return next;
    });
  }
  function addItem() {
    setTpl(t => {
      const next = { ...t, items: [...(t.items || []), { name: "New on-cost", type: "percent_of_pay", value: 0, enabled: true }] };
      scheduleTplSave(next);
      return next;
    });
  }
  function removeItem(i) {
    setTpl(t => {
      const next = { ...t, items: t.items.filter((_, j) => j !== i) };
      scheduleTplSave(next);
      return next;
    });
  }
  async function saveTpl() {
    setSavingTpl(true);
    try {
      await api.patch(`/worker-cost-templates/${tpl.id}`, {
        working_days_per_week: tpl.working_days_per_week,
        hours_per_day: tpl.hours_per_day,
        items: tpl.items,
      });
      setMsg("Template saved ✓ — cost rates will recalculate on each worker's next recompute.");
      setTimeout(() => setMsg(""), 3500);
      load();
    } finally { setSavingTpl(false); }
  }
  async function recomputeAll() {
    for (const w of workers.filter(x => !x.archived)) {
      try { await api.post(`/workers/${w.id}/recalculate-cost`, { template_id: tpl.id }); } catch {}
    }
    setMsg("All active workers recalculated from the current template ✓");
    setTimeout(() => setMsg(""), 3500);
    load();
  }

  if (!tpl) return <div className="opacity-60">Loading template…</div>;

  return (
    <div className="space-y-3" data-testid="worker-cost-calc">
      {msg && <div className="text-green-400 text-sm">{msg}</div>}

      <div className="card">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
          <div>
            <h3 className="text-silver">{tpl.name}</h3>
            <p className="text-xs opacity-70">
              Mirrors Buildaprice. Profit is kept INSIDE cost so Luke's $50/h → $74.33/h numbers match.
            </p>
          </div>
          <div className="flex gap-2 items-end flex-wrap">
            <SaveIndicator status={autosave.status} data-testid="wcc-save-indicator"/>
            <label className="text-xs">Working days / week
              <NumberInput className="input w-16" value={tpl.working_days_per_week}
                onChange={e => { const v = parseFloat(e.target.value || 0); const next = { ...tpl, working_days_per_week: v }; setTpl(next); scheduleTplSave(next); }} data-testid="tpl-days-week"/>
            </label>
            <label className="text-xs">Working hours / day
              <NumberInput className="input w-20" value={tpl.hours_per_day} step="any"
                onChange={e => { const v = parseFloat(e.target.value || 0); const next = { ...tpl, hours_per_day: v }; setTpl(next); scheduleTplSave(next); }} data-testid="tpl-hours-day"/>
            </label>
            <button className="btn-silver text-sm" onClick={saveTpl} disabled={savingTpl} data-testid="btn-save-template">Save template</button>
          </div>
        </div>

        <table className="table text-sm">
          <thead><tr><th></th><th>On-cost</th><th>Type</th><th className="text-right">Value</th><th className="text-right">% of pay</th><th className="text-right">$ / hour</th><th></th></tr></thead>
          <tbody>
          {(tpl.items || []).map((it, i) => { const c = calc?.items?.[i] || {}; return (
            <tr key={i} data-testid={`oncost-${i}`}>
              <td><input type="checkbox" checked={!!it.enabled} onChange={e => patchItem(i, { enabled: e.target.checked })} data-testid={`oncost-chk-${i}`}/></td>
              <td><input className="input" value={it.name} onChange={e => patchItem(i, { name: e.target.value })} data-testid={`oncost-name-${i}`}/></td>
              <td>
                <select className="input" value={it.type} onChange={e => patchItem(i, { type: e.target.value })} data-testid={`oncost-type-${i}`}>
                  {Object.entries(TYPE_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
              </td>
              <td><NumberInput className="input text-right w-24" value={it.value} onChange={e => patchItem(i, { value: parseFloat(e.target.value || 0) })} data-testid={`oncost-value-${i}`}/></td>
              <td className="text-right">{(c.effective_percent ?? 0).toFixed(2)}%</td>
              <td className="text-right">{fmtNZD(c.per_hour_dollar || 0)}</td>
              <td><button className="text-xs text-red-400" onClick={() => removeItem(i)} data-testid={`oncost-del-${i}`}>Delete</button></td>
            </tr>
          ); })}
          </tbody>
        </table>
        <button className="btn-ghost text-xs mt-2" onClick={addItem} data-testid="btn-add-oncost">+ Add on-cost</button>
      </div>

      <div className="card" data-testid="cost-preview">
        <div className="flex gap-3 items-end flex-wrap">
          <label className="text-sm">Pay rate ($/h)
            <NumberInput className="input w-28" money value={payRate} onChange={e => setPayRate(parseFloat(e.target.value || 0))} data-testid="calc-pay-rate"/>
          </label>
          <div className="text-sm">Total on-cost: <b className="text-silver" data-testid="calc-total-pct">{calc?.total_oncost_pct ?? 0}%</b></div>
          <div className="text-sm">Cost rate: <b className="text-silver" data-testid="calc-cost-rate">{fmtNZD(calc?.cost_rate || 0)}/h</b></div>
          <div className="text-xs opacity-70 ml-auto">Example: $50/h pay → <b className="text-silver">$74.33/h cost</b> at 48.66% on-costs.</div>
        </div>
      </div>

      <div className="card">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
          <h3 className="text-silver text-sm">Push to workers</h3>
          <button className="btn-ghost text-xs" onClick={recomputeAll} data-testid="btn-recalc-all">
            Recalculate every active worker from this template
          </button>
        </div>
        <table className="table text-sm">
          <thead><tr><th>Worker</th><th className="text-right">Pay rate</th><th className="text-right">Cost rate</th><th className="text-right">Charge-out</th><th>Source</th><th></th></tr></thead>
          <tbody>{workers.filter(w => !w.archived).map(w => (
            <tr key={w.id} data-testid={`wcc-worker-${w.id}`}>
              <td>{w.name}</td>
              <td className="text-right">{fmtNZD(w.pay_rate || 0)}</td>
              <td className="text-right">{fmtNZD(w.cost_rate || 0)}</td>
              <td className="text-right">{fmtNZD(w.charge_out_rate || 0)}</td>
              <td className="text-xs opacity-70">{w.cost_rate_source || "—"}</td>
              <td><button className="hub-link text-xs"
                   onClick={async () => { await api.post(`/workers/${w.id}/recalculate-cost`, { template_id: tpl.id }); load(); }}
                   data-testid={`btn-wcc-recalc-${w.id}`}>Recalculate</button></td>
            </tr>
          ))}</tbody>
        </table>
      </div>
    </div>
  );
}
